import React, { useState, useCallback } from "react"
import styled from "styled-components"
import { Swiper, SwiperSlide } from "swiper/react"
import { A11y, Navigation, Pagination } from "swiper/modules"
import { useMedia } from "react-use"
import "swiper/css"
import "swiper/css/a11y"
import "swiper/css/navigation"
import "swiper/css/pagination"

import ImageContentful from "../ImageContentful"
import YouTubePlayer from "../YouTubePlayer"
import TestimonialAuthor from "../TestimonialAuthor"
import { media, mixins } from "../../styles"

const Wrapper = styled.div`
  margin: 0 0 20px;

  @media ${media.md} {
    margin-bottom: 10px;
  }

  @media ${media.lg} {
    margin-bottom: 40px;
  }

  .swiper {
    padding-bottom: 40px;

    @media ${media.md} {
      padding-bottom: 50px;
    }
  }

  .swiper-button-next,
  .swiper-button-prev {
    color: var(--color-spaceblue);
    margin-top: -50px;

    @media ${media.smDown} {
      display: none;
    }
  }

  .swiper-button-next {
    @media ${media.md} {
      right: 20px;
    }
  }

  .swiper-button-prev {
    @media ${media.md} {
      left: 20px;
    }
  }

  .swiper-button-disabled {
    display: none;
  }

  .swiper-slide {
    padding: 0 10px;

    @media ${media.md} {
      padding: 0 25px;
    }

    @media ${media.lg} {
    }

    @media ${media.xl} {
      padding: 0 50px;
    }
  }
`

const SlideWrapper = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  gap: 30px;
  outline-offset: -1px !important;

  @media ${media.md} {
    flex-direction: row;
  }

  &:after {
    /* Disable clicks in inactive slides */
    content: "";
    cursor: pointer;
    display: block;
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
    z-index: 10;

    .swiper-slide-active & {
      display: none;
    }
  }
`

const SlidePlayer = styled.div`
  @media ${media.md} {
    flex: 1 0 50%;
    padding: 0 0 0 20px;
  }

  @media ${media.lg} {
    padding: 0 20px;
  }
`

const SlideContent = styled.div`
  padding: 0 10px;

  @media ${media.md} {
    display: flex;
    flex: 1 0 50%;
    flex-direction: column;
    justify-content: center;
    padding: 0 40px 0 20px;
  }
`

const Logo = styled.div`
  bottom: 5px;
  left: 5px;
  position: absolute;
  width: 100px;
  z-index: 2;

  .wrap--svg {
    position: relative;
    z-index: 1;
  }

  &::after {
    background: black;
    border-radius: 100%;
    bottom: 0;
    content: "";
    filter: blur(10px);
    left: 0;
    opacity: 0.15;
    position: absolute;
    right: 0;
    top: 0;
    z-index: 0;
  }

  @media ${media.sm} {
    left: 20px;
    bottom: 20px;
    width: 130px;
  }

  @media ${media.md} {
    left: 10px;
    bottom: 10px;
    width: 100px;
  }

  @media ${media.lg} {
    left: 20px;
    bottom: 20px;
    width: 130px;
  }

  @media ${media.xl} {
    left: 15px;
    bottom: 10px;
    width: 160px;
  }

  svg {
    color: var(--color-white);
    height: auto;
    width: 100%;
  }

  img {
    max-height: 100%;
    max-width: 100%;
    object-fit: contain !important;
  }
`

const Quote = styled.blockquote`
  ${mixins.copy2}
  margin: 0 0 40px;
  padding: 0;
`

const Testimonials = ({ testimonials }) => {
  const [, updateState] = useState()
  const forceUpdate = useCallback(() => updateState({}), [])
  const prefersReducedMotion = useMedia(
    "(prefers-reduced-motion: reduce)",
    false
  )

  const sliderParams = {
    a11y: {
      enabled: true,
    },
    centeredSlides: true,
    modules: [A11y, Navigation, Pagination],
    navigation: true,
    onSlideChange: () => {
      forceUpdate()
    },
    pagination: {
      type: "bullets",
      clickable: true,
    },
    slidesPerView: "auto",
    slideToClickedSlide: true,
    speed: prefersReducedMotion ? 0 : 300,
  }

  return (
    <Wrapper>
      <Swiper {...sliderParams}>
        {testimonials.map(
          (testimonial, i) =>
            testimonial?.id && (
              <SwiperSlide key={i}>
                <SlideWrapper tabIndex="0">
                  <SlidePlayer>
                    {testimonial?.youtubeURL && (
                      <YouTubePlayer
                        url={testimonial.youtubeURL}
                        thumbnail={
                          <>
                            <Logo>
                              <ImageContentful
                                alt={testimonial.company.name}
                                image={
                                  testimonial.company.logoInlined ||
                                  testimonial.company.logo
                                }
                                inline
                              />
                            </Logo>
                            <ImageContentful
                              alt="Video"
                              image={testimonial.thumbnail}
                            />
                          </>
                        }
                        showPlayIcon
                      />
                    )}
                  </SlidePlayer>
                  <SlideContent>
                    <Quote>“{testimonial.text.text}”</Quote>
                    <TestimonialAuthor
                      name={testimonial.authorName}
                      photo={testimonial.authorPhoto}
                      title={testimonial.authorTitle}
                    />
                  </SlideContent>
                </SlideWrapper>
              </SwiperSlide>
            )
        )}
      </Swiper>
    </Wrapper>
  )
}

export default Testimonials
